import React, { useEffect, useState } from 'react';
import './App.css';
import { CUSTOMER_NAME, TEST_MODE } from './env';
import { runInference } from './api';
import ResultImage from './ResultImage';
import TestPanel from './TestPanel';

function App() {
  const [testMode, setTestMode] = useState(TEST_MODE);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [results, setResults] = useState<unknown>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(selectedFile);
    setImageName(null);
    setResults(null);
    setPreviewUrl(selectedFile ? URL.createObjectURL(selectedFile) : null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) {
      setError('Please select an image first.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    setImageName(null);
    setResults(null);

    try {
      const { results: parsedResults, imageName: storedName } = await runInference(file);
      setImageName(storedName);
      setResults(parsedResults);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-shell">
      <header className="hero">
        <p
          className="eyebrow"
          onDoubleClick={() => setTestMode((v) => !v)}
          title="Double-click to toggle test mode"
        >
          Vision Inference{testMode ? ' · Test mode' : ''}
        </p>
        <h1>{CUSTOMER_NAME}</h1>
        <p className="subtitle">
          Upload an image, preview it, and send it to the inference service to see detected objects.
        </p>
        <dl className="env-hints">
          <div>
            <dt>Inference endpoint</dt>
            <dd>{`${window.location.origin}/inference`}</dd>
          </div>
        </dl>
      </header>

      <main className="content">
        <>
        {!testMode && (
        <section className="card">
          <form className="upload-form" onSubmit={handleSubmit}>
            <label className="field">
              <span className="label">Select an image</span>
              <input type="file" accept="image/*" onChange={handleFileChange} />
            </label>

            {previewUrl && (
              <div className="preview">
                <span className="preview-label">Preview</span>
                <div className="preview-frame">
                  <img src={previewUrl} alt="Selected preview" />
                </div>
              </div>
            )}

            {error && <p className="message error">{error}</p>}

            <button className="primary" type="submit" disabled={isSubmitting || !file}>
              {isSubmitting ? 'Sending…' : 'Send to inference'}
            </button>
          </form>
        </section>
        )}

        {!testMode && results != null && (
          <section className="card results">
            <div className="result-block">
              <div className="result-header">
                <h2>Inference output</h2>
                <p>
                  Returned visualization and raw response from the detection service once processing
                  completes.
                </p>
              </div>

              <ResultImage imageName={imageName} alt="Inference result" />

              <div className="json-output">
                <div className="json-header">Response JSON</div>
                <pre>{JSON.stringify(results, null, 2)}</pre>
              </div>
            </div>
          </section>
        )}

        {testMode && <TestPanel />}
        </>
      </main>
    </div>
  );
}

export default App;
